<template>
    <BaseModal
        v-if="show"
        :title="isEditing ? 'Edit Permission' : 'Create Permission'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="2xl"
        @close="emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Permission Name
                </label>
                <input
                    v-model="formData.name"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-red-500': errors.name }"
                    placeholder="Enter permission name"
                />
                <p
                    v-if="errors.name"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.name }}
                </p>
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Guard
                </label>
                <select
                    v-model="formData.guard_name"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-red-500': errors.guard_name }"
                >
                    <option value="web">Web</option>
                    <option value="api">API</option>
                </select>
                <p
                    v-if="errors.guard_name"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.guard_name }}
                </p>
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Group
                </label>
                <input
                    v-model="formData.group_name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    :class="{ 'border-red-500': errors.group_name }"
                    placeholder="Enter group name (optional)"
                />
                <p
                    v-if="errors.group_name"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.group_name }}
                </p>
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Assign to Roles
                </label>
                <div
                    class="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-48 overflow-y-auto p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <label
                        v-for="role in allRoles"
                        :key="role.id"
                        class="flex items-center space-x-2 p-2 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-blue-50 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                    >
                        <input
                            type="checkbox"
                            :value="role.id"
                            v-model="formData.role_ids"
                            class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div class="flex-1">
                            <div
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ role.name }}
                            </div>
                            <div
                                class="text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{ role.guard_name }}
                            </div>
                        </div>
                    </label>
                </div>
                <p
                    v-if="!isEditing && formData.role_ids.length === 0"
                    class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                >
                    Tip: Optionally assign this permission to one or more roles
                </p>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from "vue";
import { usePermissionStore } from "@/stores/permission";
import { useRoleStore } from "@/stores/role";
import type { Permission } from "@/api/permission";
import type { Role } from "@/api/role";
import BaseModal from "@/components/BaseModal.vue";
import roleApi from "@/api/role";
import permissionApi from "@/api/permission";

const props = defineProps<{
    show: boolean;
    permission?: Permission | null;
    isEditing: boolean;
}>();

const emit = defineEmits<{
    close: [];
    saved: [];
}>();

const permissionStore = usePermissionStore();
const roleStore = useRoleStore();

const loading = ref(false);
const errors = ref<Record<string, string>>({});
const allRoles = ref<Role[]>([]);
const formData = ref({
    name: "",
    guard_name: "web",
    group_name: "",
    role_ids: [] as number[],
});

const fetchRoles = async () => {
    try {
        const response = await roleApi.getRoles({ per_page: 100 });
        allRoles.value = response.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch roles:", error);
    }
};

watch(
    [() => props.show, () => props.permission],
    async ([newShow, newPermission]) => {
        errors.value = {};
        if (newShow && newPermission) {
            // For edit mode, load all roles
            await fetchRoles();

            // Find roles that have this permission
            const roleIdsWithPermission: number[] = [];
            for (const role of allRoles.value) {
                try {
                    const roleResponse = await roleApi.getRole(role.id);
                    const hasPermission = roleResponse.data.permissions?.some(
                        (p: any) => p.id === newPermission.id,
                    );
                    if (hasPermission) {
                        roleIdsWithPermission.push(role.id);
                    }
                } catch (error) {
                    console.error(`Failed to check role ${role.id}:`, error);
                }
            }

            nextTick(() => {
                formData.value = {
                    name: newPermission.name || "",
                    guard_name: newPermission.guard_name || "web",
                    group_name: newPermission.group_name || "",
                    role_ids: roleIdsWithPermission,
                };
            });
        } else if (newShow) {
            // For create mode, load all roles
            await fetchRoles();
            nextTick(() => {
                formData.value = {
                    name: "",
                    guard_name: "web",
                    group_name: "",
                    role_ids: [],
                };
            });
        }
    },
    { immediate: true, deep: true },
);

const handleSubmit = async () => {
    if (loading.value) return;

    loading.value = true;
    errors.value = {};
    try {
        let permissionId: number;

        if (props.isEditing && props.permission) {
            await permissionStore.updatePermission(props.permission.id, {
                ...formData.value,
            });
            permissionId = props.permission.id;
        } else {
            const result = await permissionStore.createPermission({
                ...formData.value,
            });
            // Get the ID from created permission
            permissionId = result.data?.id || props.permission?.id || 0;
        }

        // Assign permission to selected roles
        if (formData.value.role_ids.length > 0 && permissionId) {
            for (const roleId of formData.value.role_ids) {
                try {
                    // Get current role permissions
                    const roleResponse = await roleApi.getRole(roleId);
                    const currentPermissionIds =
                        roleResponse.data.permissions?.map((p: any) => p.id) ||
                        [];

                    // Add new permission if not already present
                    if (!currentPermissionIds.includes(permissionId)) {
                        await roleApi.assignPermissions(roleId, [
                            ...currentPermissionIds,
                            permissionId,
                        ]);
                    }
                } catch (error) {
                    console.error(
                        `Failed to assign permission to role ${roleId}:`,
                        error,
                    );
                }
            }
        }

        formData.value = {
            name: "",
            guard_name: "web",
            group_name: "",
            role_ids: [],
        };
        errors.value = {};
        emit("saved");
    } catch (error: any) {
        if (error.response?.status === 422) {
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                errors.value[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
            return;
        }
        throw error;
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchRoles();
});
</script>
